import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, Form, Input,
} from 'antd';
import {
  BusinessPartnerWithContracts, ContractCreateParams, useBusinessSyncWithMBS,
} from '../../../../../../hooks/api/businessPartners';
import { dateToDisplay, dateToSave } from '../../../../../../utils/date';
import { GetTariffApplicationData, IContract, useGetTariffApplication } from '../../../../../../hooks/api/contracts';
import { getBankIdentifier } from '../../../../../../utils/iban';
import { useCheckBank, useUpdateBankAccount } from '../../../../../../hooks/api/bankAccount';

import DetailList from '../../../../../Common/DetailList';
import { filterObjectProps, isObjEqual } from '../../../../../../utils';

const initialState: ContractCreateParams = {
  paymentMethod: {
    accountOwnerName: '',
    contractNumber: '',
    customerInformed: true,
    iban: '',
    periodStart: dateToSave(),
  },
};

const prepareValues = ({ contract, businessPartnerData }: CollapseContractProps) => {
  const { contractNumber } = contract;

  const bankAccounts = businessPartnerData?.bankAccounts || [];
  // const latestBankAccount = bankAccounts[bankAccounts.length - 1];
  const latestBankAccount = bankAccounts[0]; // First item is latest added

  return {
    paymentMethod: {
      accountOwnerName: latestBankAccount?.ownerName,
      contractNumber,
      customerInformed: true,
      iban: latestBankAccount?.iban,
      periodStart: dateToSave(),
    },
  };
};

interface CollapseContractProps {
  businessPartnerData: BusinessPartnerWithContracts | undefined;
  contract: IContract;
  isSyncRequired?: boolean;
}

export default function CollapseItem({ businessPartnerData, contract, isSyncRequired }: CollapseContractProps) {
  const checkIban = useCheckBank();
  const getTariffApplication = useGetTariffApplication(contract.contractNumber);
  const updateBankAccount = useUpdateBankAccount();
  const businessSyncWithMBS = useBusinessSyncWithMBS();
  const [form] = Form.useForm();

  const [tariffData, setTariffData] = useState<GetTariffApplicationData | null>(null);
  const [fieldsChanged, setFieldsChanged] = useState(false);

  const syncBusinessFetch = () => {
    if (businessPartnerData?.businessPartnerNumber) {
      businessSyncWithMBS.fetch(undefined, `${businessPartnerData?.businessPartnerNumber}/parse`);
    }
  };

  useEffect(() => {
    if (contract.contractNumber) {
      getTariffApplication.fetch({ contractNumber: contract.contractNumber });
    }
  }, [contract.contractNumber]);

  useEffect(() => {
    if (getTariffApplication.data?.tariffId) {
      setTariffData(getTariffApplication.data);
    }
  }, [getTariffApplication.data]);

  /** Initial values prep */
  const [initialValues, setInitialValues] = useState(initialState); // <User>

  useEffect(() => {
    if (contract) {
      setInitialValues(prepareValues({ contract, businessPartnerData }));
    }
  }, [contract]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setFieldsChanged(false);
  }, [initialValues]);

  /** Submit */
  const handleSubmit = (values: ContractCreateParams) => {
    setFieldsChanged(false);

    const {
      contractNumber, customerInformed, accountOwnerName, iban, periodStart,
    } = values.paymentMethod;

    const payloadPayment = {
      contractNumber,
      customerInformed,
      accountOwnerName,
      iban,
      periodStart,
    };

    updateBankAccount.fetch(payloadPayment)
      .then((res) => {
        if (!(res as any).error) {
          syncBusinessFetch();
        }
      });
  };

  /** Check bank feature */
  const ibanWatch = Form.useWatch(['paymentMethod', 'iban'], form);

  useEffect(() => {
    if (ibanWatch) {
      const callback = () => {
        const bankIdentifier = getBankIdentifier(ibanWatch);

        if (bankIdentifier) {
          checkIban.fetch({ bankIdentifier });
        }
      };
      const timeoutId = setTimeout(callback, 500);

      return () => clearTimeout(timeoutId);
    }
    if (!ibanWatch) {
      form.setFieldValue(['paymentMethod', 'bankName'], '');
    }

    return undefined;
  }, [ibanWatch]);

  useEffect(() => {
    if (checkIban.data && !checkIban.error) {
      form.setFieldValue(['paymentMethod', 'bankName'], checkIban.data.bank);
    }
  }, [checkIban.data, checkIban.error]);

  const handleFieldsChange = () => {
    const formValues: ContractCreateParams = form.getFieldsValue();

    if (!initialValues || !formValues.paymentMethod) return;
    setFieldsChanged(!isObjEqual(
      initialValues,
      {
        ...formValues,
        paymentMethod: filterObjectProps(formValues.paymentMethod, ['bankName']),
      },
    ));
  };

  /** --- */
  const contractInfoValues = useMemo(() => [
    { label: 'Period start', value: dateToDisplay(contract?.periodStart) },
    { label: 'State', value: contract?.state },
    { label: 'Tariff Name', value: contract?.tariffName },
    // { label: 'Tariff ID', value: tariffData?.tariffId },
    { label: 'Tariff ID', value: contract?.tariffId || tariffData?.tariffId },
  ], [contract, tariffData]);

  return (
    <Form
      form={form}
      name="business-form-contracts"
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
      onFieldsChange={handleFieldsChange}
      disabled={isSyncRequired}
    >
      <DetailList items={contractInfoValues} />

      <Form.Item name={['paymentMethod', 'contractNumber']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['paymentMethod', 'customerInformed']} hidden>
        <Input />
      </Form.Item>
      <Form.Item
        label="Account Owner Name"
        name={['paymentMethod', 'accountOwnerName']}
        rules={[{ required: true, message: 'Please enter the account owner name' }]}
      >
        <Input placeholder="Enter" />
      </Form.Item>
      <Form.Item
        label="IBAN"
        name={['paymentMethod', 'iban']}
        rules={[{ required: true, message: 'Please enter IBAN' }]}
      >
        <Input placeholder="Enter" />
      </Form.Item>

      <Form.Item
        label="Bank name"
        name={['paymentMethod', 'bankName']}
      >
        <Input placeholder="Enter" />
      </Form.Item>

      <Form.Item name={['paymentMethod', 'periodStart']} hidden>
        <Input disabled />
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={updateBankAccount.loading || businessSyncWithMBS.loading}
          disabled={!fieldsChanged}
        >
          Save Contract
        </Button>
      </div>
    </Form>
  );
}
